import { Building, Property, Site } from "../models/Property";

export function toFormattedString(val: number) {
	if (val) {
		return val.toString().replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1,");
	}
}

export function utilityStringBuilder(provider: string | undefined, info: string | undefined): string | null {
	let information = null;
	if (provider) {
		information = provider;
	}
	if (info) {
		information = information ? information + ' - ' + info : info;
	}

	return information;
}

function checkFields(value: Partial<Building> | Partial<Site>): boolean {
	return !Object.values(value).every(x => (x === null || x === '' || x === undefined));
}

export function displayBuilding(property: Property): boolean {
	if (!property.building) return false;

	const building: Partial<Building> = {
		tenancy: property.building.tenancy,
		areaSqFt: property.building?.areaSqFt,
		buildingType: property.building?.buildingType,
		officeSqFt: property.building?.officeSqFt,
		numberOfDockDoors: property.building?.numberOfDockDoors,
		numberOfDriveInDoors: property.building?.numberOfDriveInDoors
	};

	return checkFields(building);
}

/**
 * Utility function to check if Property has a value for acres 
 * or building square footage, then returns a formatted string.
 */
export function propertySizeFormatter(property: Property, detailItem: boolean = false): string | undefined {
	let propertySize;
	const propertyType = property.isSiteOrBuilding?.toLowerCase();
	if (!propertyType) return propertySize;


	if (propertyType === 'site' && property.site?.areaAcres) {
		const acresPrefix = !detailItem ? 'Property Area Acres' : 'Property Acres';
		propertySize = `${acresPrefix}: ${toFormattedString(property?.site?.areaAcres)}`;
	} else if (propertyType === 'building' && property.building?.areaSqFt) {
		const sqFtPrefix = !detailItem ? 'Building Square Footage' : 'Building Sq Ft';
		propertySize = `${sqFtPrefix}: ${toFormattedString(property.building.areaSqFt)}`;
	} else if (propertyType === 'both') {

		if (property.site?.areaAcres) {
			const acresPrefix = !detailItem ? 'Property Area Acres' : 'Property Acres';
			propertySize = `${acresPrefix}: ${toFormattedString(property.site.areaAcres)}`;
		} else if (property.building?.areaSqFt) {
			const sqFtPrefix = !detailItem ? 'Building Square Footage' : 'Building Sq Ft';
			propertySize = `${sqFtPrefix}: ${toFormattedString(property.building.areaSqFt)}`;
		} else { propertySize = undefined; }
	} else { propertySize = undefined; }

	return propertySize;
}

export function propertySaleStatus(property: Property, detailItem: boolean = false) {
	let propertyStatus: string = property.site?.siteSaleOrLease ? property.site?.siteSaleOrLease : '';
	if (propertyStatus.toLowerCase().split(' ').includes('both')) {
		propertyStatus = 'For Sale/Lease';
	}

	if (!detailItem) {
		propertyStatus = ' - ' + propertyStatus;
	}

	return propertyStatus;
}

export function propertyNameFormatter(property: Property): string {
	let propertyName = property.siteBuildingName!;
	if (
		property.isSiteOrBuilding?.toLowerCase() === "building"
		&& property.building?.buildingName
	) {
		propertyName += ` - ${property.building?.buildingName}`;
	}
	if (propertyName != null) {
		return propertyName;
	}
	return propertyName;
}

export function propertyHighwayFormatter(property: Property): string | undefined {
	let nearestHighway;

	if (property.nearestHighway) {
		nearestHighway = property.nearestHighway;
	}
	/*if (property.highwayMiles) {
	  nearestHighway = nearestHighway
		? `${nearestHighway} - ${property.highwayMiles} miles away`
		: `${property.highwayMiles} miles away`;
	}*/

	return nearestHighway;
}

export function zoningFormatter(zoned: Site | Building) {
	let zoning: string | undefined;

	if (zoned.zoning) {
		if (zoned.zoning.toLowerCase() === "other") {
			zoning = zoned.zoningOther;
		} else {
			zoning = zoned.zoning.replaceAll("_", " ").replaceAll(",", ", ");
		}
	}

	return zoning;
}

export function specialtyFeaturesFormatter(specialtyFeatured: Site | Building): string {
    let specialties = "";

    if (Array.isArray(specialtyFeatured.specialtyFeatures) && specialtyFeatured.specialtyFeatures.some(f => f?.trim())) {
        const features = specialtyFeatured.specialtyFeatures
            .filter(f => f?.trim())
            .map((f) =>
                f.trim()
                    .split("_")
                    .map((w) => w[0].toUpperCase() + w.slice(1))
                    .join(" ")
            );

        if (features.length) {
            specialties = features.join(", ");
        }
    }

    return specialties;
}




export function incentiveFormatter(incentivesString: string) {
	let incentives: string | undefined;
	if (!incentivesString || typeof incentivesString !== "string") {
		return;
	}
	const incentivesArray = incentivesString.split(',');
  
	// Helper function to check if a feature exists in the specialtyFeatures array
	const incentiveExists = (feature: string): boolean | undefined => {
	  return incentivesArray?.some(
		(f) => f.trim().toLowerCase() === feature.toLowerCase()
	  );
	};
  
	if (incentiveExists('Opportunity_Zone')) {
	  incentives = 'Opportunity Zone';
	}

	// /* OLD CODE
	// if (specialtyFeatured?.specialtyFeatures?.includes('enterprise_zone')) {
	// 	if (incentives) incentives += ', Enterprise Zone';
	// 	else incentives = 'Enterprise Zone';
	// }*/
  
	if (incentiveExists('Foreign_Trade_Zone')) {
	  if (incentives) incentives += ', Foreign Trade Zone';
	  else incentives = 'Foreign Trade Zone';
	}
  
	return incentives;
  }

function _amenitiesStringBuilder(amenities: string | null) {
	return amenities 
	? (amenities as string)
			.split(',')
			.map(t => t.trim())
			.join(', ')
	: amenities;
}

/**
 * Fixes spacing issues with additional amenities data.
 */
export function additionalAmenitiesFormatter(amenities: string | null, amenitiesOther: string | null) {
	let a = _amenitiesStringBuilder(amenities);
	let b = amenitiesOther = _amenitiesStringBuilder(amenitiesOther);

	let output = null;
	if (a) {
		output = a;
	}

	if (b && output) {
		output += `, ${b}`;
	} else if (b) {
		output = b;
	}

	return output;
}

export function checkWatcoContact(watcoContact: string | null | undefined, watcoContactOther: string | null | undefined) {
	if (watcoContact && watcoContact !== 'other' && watcoContact !== 'Other') {
    return watcoContact;
  } else if (watcoContactOther) {
    return watcoContactOther;
  } else {
    return "";
  }
}

export function propertyDocumentCount(property: Property) {
	// Ensure brochureUrl is a valid string before splitting
	if (!property.brochureUrl || property.brochureUrl === '') {
	  return 0;
	} else {
	  return property.brochureUrl.split(',').length;
	}
}

export function checkDescriptionLength(property: Property): boolean {
	if (!property) return false;

	const {
		watcoContact,
		watcoContactEmail,
		watcoContactPhone,
		brokerContact,
		brokerContactPhone,
		brokerEmail,
		companyName,
		siteBuildingDescription,
	} = property;

	const safeString = (value: string | null | undefined) => value ?? '';

	const totalLength =
		safeString(watcoContact).length +
		safeString(watcoContactEmail).length +
		safeString(watcoContactPhone).length +
		safeString(brokerContact).length +
		safeString(brokerContactPhone).length +
		safeString(brokerEmail).length +
		safeString(companyName).length +
		safeString(siteBuildingDescription).length;

	return totalLength <= 400;
}

